import { Link } from "react-router";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import type { BlockContentMap, ServiceSummary } from "@veo/types";
import { cn } from "@veo/ui";
import { asServices } from "../lib/section-data";
import { RevealItem, RevealList } from "../components/motion";
import { EmptyState } from "../components/Skeletons";
import {
  SectionHeading,
  SectionShell,
  isDarkBackground,
  type SectionProps,
} from "./SectionRenderer";

/**
 * How wide one card is, for a row of n.
 *
 * Widths rather than grid columns because the row is a flex wrap: a grid puts a
 * trailing item in column one and leaves the rest of the row empty, which with
 * five services across four columns is one card adrift on the left under three
 * card-widths of nothing. Wrapping with `justify-center` centres the remainder
 * instead and splits the leftover space either side of it.
 *
 * Each width subtracts its share of the gutter — `gap-6` is 1.5rem, and n per
 * row lose (n-1)/n of it each. Written out per count because Tailwind emits
 * only the classes it can read as literal text in the source.
 *
 * Two across from `sm` in every case; four across only from `lg`, below which
 * a card carrying an image, a heading and a five-line summary has no room.
 */
const CARD_WIDTHS: Record<number, string> = {
  1: "sm:w-[calc(50%-0.75rem)]",
  2: "sm:w-[calc(50%-0.75rem)]",
  3: "sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]",
  4: "sm:w-[calc(50%-0.75rem)] lg:w-[calc(25%-1.125rem)]",
};

/**
 * How many cards sit on a row: what the editor asked for, unless that would
 * leave exactly one card by itself on the last row.
 *
 * The editor chooses a column count for the block, not for a service list that
 * grows every time someone publishes. Five services under a four-column setting
 * is the case this exists for — three then two reads as a deliberate shape,
 * four then one reads as a mistake. Stepping down only helps if the narrower
 * row does not strand a card in turn, hence the second remainder check.
 *
 * Fewer services than columns simply use the smaller number, so two services
 * are two half-width cards centred rather than two narrow ones hugging the
 * left edge.
 */
function cardsPerRow(columns: number, count: number) {
  const fit = Math.min(columns, count);

  if (fit >= 3 && count > fit && count % fit === 1 && count % (fit - 1) !== 1) {
    return fit - 1;
  }

  return fit;
}

/**
 * The services grid.
 *
 * `serviceIds` empty means "every published service, in the order an editor
 * arranged them" — which is what an editor expects from an untouched block, and
 * means a newly published service appears here without anyone remembering to
 * add it.
 */
export default function ServicesGridBlock({
  content,
  settings,
  data,
}: SectionProps<BlockContentMap["servicesGrid"]>) {
  const onDark = isDarkBackground(settings);
  const services = asServices(data);

  const selected =
    services === null
      ? []
      : content.serviceIds.length > 0
        ? content.serviceIds
            .map((id) => services.find((service) => service.id === id))
            .filter((service): service is ServiceSummary => Boolean(service))
        : services;

  const perRow = cardsPerRow(content.columns, selected.length);
  const widths = CARD_WIDTHS[perRow] ?? CARD_WIDTHS[3];

  return (
    <SectionShell settings={settings}>
      <SectionHeading
        eyebrow={content.eyebrow}
        heading={content.heading}
        intro={content.intro}
        settings={settings}
      />

      {selected.length === 0 && (
        <EmptyState
          title={services === null ? "Services could not be loaded" : "No services yet"}
          description={
            services === null
              ? "Please refresh the page, or get in touch and we will talk you through them."
              : "Our services are being updated. Please check back shortly."
          }
        />
      )}

      {selected.length > 0 && (
        <RevealList className="flex flex-wrap justify-center gap-6">
          {selected.map((service) => {
            const Icon =
              (Icons[(service.icon ?? "Circle") as keyof typeof Icons] as typeof Icons.Circle) ??
              Icons.Circle;

            return (
              // The item is itself a flex container so the card stretches to the
              // tallest in its row whatever wraps it, rather than leaving the
              // short ones floating with a ragged bottom edge.
              <RevealItem key={service.id} className={cn("flex w-full", widths)}>
                <article
                  className={cn(
                    "group flex w-full flex-col overflow-hidden rounded-xl transition-shadow",
                    onDark
                      ? "border border-navy-600 bg-navy-800 hover:border-gold-500/50"
                      : "border border-ink-200 bg-white shadow-card hover:shadow-lg",
                  )}
                >
                  {service.cardImageUrl && (
                    <div className="aspect-[16/10] overflow-hidden bg-navy-900">
                      <img
                        src={service.cardImageUrl}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none"
                      />
                    </div>
                  )}

                  <div className="flex flex-1 flex-col p-7">
                    {content.showIcon && (
                      <span className="inline-flex w-fit rounded-md bg-navy-900 p-3 text-gold-300">
                        <Icon aria-hidden className="size-6" />
                      </span>
                    )}

                    <h3
                      className={cn(
                        "mt-5 font-display text-xl",
                        onDark ? "text-cream-50" : "text-navy-900",
                      )}
                    >
                      {service.name}
                    </h3>

                    {content.showSummary && (
                      /*
                        Capped at five lines so a card is a preview, not the
                        page. Service summaries are written as full paragraphs
                        and vary by a hundred characters between services, which
                        left one card towering over its neighbours.

                        `flex-1` on the paragraph, rather than a fixed height:
                        the text block absorbs whatever slack the grid row has,
                        so the link below it lands on the same line across every
                        card even when one summary runs to three lines and
                        another to five.
                      */
                      <p
                        className={cn(
                          // `text-wrap` overrides the base layer's
                          // `text-wrap: pretty` on paragraphs. Pretty avoids
                          // orphans by shortening the last line — sensible in
                          // prose, wrong under a clamp, where that shortened
                          // line is where the ellipsis goes. The result is an
                          // ellipsis stranded mid-line rather than sitting
                          // after the last word it could fit.
                          "mt-3 flex-1 leading-relaxed line-clamp-5 text-wrap",
                          onDark ? "text-navy-200" : "text-ink-600",
                        )}
                      >
                        {service.summary}
                      </p>
                    )}

                    {service.minInvestment && (
                      <p
                        className={cn(
                          "mt-5 text-xs font-semibold uppercase tracking-wider",
                          onDark ? "text-gold-300" : "text-gold-700",
                        )}
                      >
                        {service.minInvestment}
                      </p>
                    )}

                    {content.showLink && (
                      <Link
                        to={`/services/${service.slug}`}
                        className={cn(
                          "mt-5 inline-flex items-center gap-2 text-sm font-semibold transition-colors",
                          onDark
                            ? "text-gold-300 hover:text-gold-200"
                            : "text-navy-700 hover:text-navy-900",
                        )}
                      >
                        {content.linkLabel}
                        <ArrowRight aria-hidden className="size-4" />
                        {/* Names the destination for a screen reader reading links
                          out of context, where "Learn more" alone is useless. */}
                        <span className="sr-only">about {service.name}</span>
                      </Link>
                    )}
                  </div>
                </article>
              </RevealItem>
            );
          })}
        </RevealList>
      )}
    </SectionShell>
  );
}
